import { CXRecordLicences } from '../constants/CXRecordLicences'
import { ReportingAccessLevels } from '../constants/Permissions'
import { SelectOptionUtils } from './SelectOptionUtils'

/** i18n key prefix for CX Record licence labels (matches {@link CXRecordLanguage} entries). */
export const CX_RECORD_LICENCE_I18N_PREFIX: string = 'CX_Record_Licences_'

export type CxRecordLicenceSelectOption = {
    readonly label: string
    readonly value: CXRecordLicences
}

const CX_RECORD_LICENCE_OPTION_SOURCE: readonly CXRecordLicences[] = [
    CXRecordLicences.NONE,
    CXRecordLicences.CXR_USER,
    CXRecordLicences.CXR_MANAGER
]

/**
 * Whether the reporting level maps to a CXR supervisor tier. *
 * Accepts add-user reporting level strings (e.g. {@link ReportingAccessLevels.CALL_CENTRE_SUPERVISOR}). *
 * @param userType - Current reporting level string from the form.
 * @returns True when the user is a supervisor-tier CXR subscriber.
 */
export function isCxrSupervisorLevel(userType: string): boolean {
    return userType === ReportingAccessLevels.CALL_CENTRE_SUPERVISOR || userType === ReportingAccessLevels.CXA_ADVANCED
}

/**
 * Whether the reporting level permits the CXR Manager tier. *
 * Per the CXA↔CXR matrix (APD-12899), CXR Manager is only assignable when the user has no reporting
 * licence ({@link ReportingAccessLevels.NONE}) or a supervisor tier (Manager / CXA Advanced). All other
 * levels (Historic, Real-Time, Agent) may hold CXR Record/User only. *
 * @param userType - Current reporting level string from the form.
 * @returns True when the CXR Manager option may be offered for the reporting level.
 */
export function isCxrManagerAllowedLevel(userType: string): boolean {
    return userType === ReportingAccessLevels.NONE || isCxrSupervisorLevel(userType)
}

/**
 * Builds CXR subscription select options: labels from i18n keys, values as enum values stored on forms/API. *
 * @param getLabel - Resolves an i18n key (e.g. {@link CX_RECORD_LICENCE_I18N_PREFIX} + enum value) to a label.
 * @returns Options suitable for select components; values match {@link CXRecordLicences} runtime strings.
 */
export function buildCxrLicenceOptions(getLabel: (i18nKey: string) => string): CxRecordLicenceSelectOption[] {
    // Cast needed: mapLanguageFileToSelectionOptions returns mutable props; CxRecordLicenceSelectOption uses readonly.
    const options: CxRecordLicenceSelectOption[] = SelectOptionUtils.mapLanguageFileToSelectionOptions(
        CX_RECORD_LICENCE_OPTION_SOURCE,
        CX_RECORD_LICENCE_I18N_PREFIX,
        getLabel
    ) as CxRecordLicenceSelectOption[]

    return options
}

/**
 * Keeps only None and CXR User options for agent-tier users.
 * CXR Manager is restricted to supervisor-tier users.
 *
 * @param options - Full CXR subscription option list.
 * @returns Filtered options (None, CXR User).
 */
export function filterCxrAgentOnlyOptions(
    options: readonly CxRecordLicenceSelectOption[]
): CxRecordLicenceSelectOption[] {
    return options.filter(
        (option: CxRecordLicenceSelectOption): boolean => option.value !== CXRecordLicences.CXR_MANAGER
    )
}

/**
 * Builds CXR subscription options for the add-user details tab and the licence management page. *
 * Levels that permit CXR Manager ({@link isCxrManagerAllowedLevel}: None or supervisor tier) receive the
 * full option list; all other levels (Historic, Real-Time, Agent) receive a filtered list without
 * {@link CXRecordLicences.CXR_MANAGER}. *
 * @param userType - Reporting user type / access level string (see {@link ReportingAccessLevels}).
 * @param getLabel - i18n resolver for {@link CX_RECORD_LICENCE_I18N_PREFIX} keys.
 * @returns Select options (values are {@link CXRecordLicences} runtime strings).
 */
export function cxrSelectOptionsForAddUser(
    userType: string,
    getLabel: (i18nKey: string) => string
): CxRecordLicenceSelectOption[] {
    const allOptions: CxRecordLicenceSelectOption[] = buildCxrLicenceOptions(getLabel)

    if (isCxrManagerAllowedLevel(userType)) return allOptions

    return filterCxrAgentOnlyOptions(allOptions)
}

/**
 * Ensures an existing backend CXR subscription remains visible in a filtered select option list. *
 * When the current option list does not contain the loaded backend value, the missing option is appended using
 * the standard CX Record licence label so edit forms can still display that historic selection. *
 * @param options - Current visible options for the select.
 * @param currentValue - Existing backend CXR subscription value to preserve.
 * @param getLabel - i18n resolver for {@link CX_RECORD_LICENCE_I18N_PREFIX} keys.
 * @returns The original options, or the original options plus the missing current option.
 */
export function ensureCurrentCxrOptionVisible(
    options: readonly CxRecordLicenceSelectOption[],
    currentValue: Definable<CXRecordLicences>,
    getLabel: (i18nKey: string) => string
): CxRecordLicenceSelectOption[] {
    if (
        !currentValue ||
        options.some((option: CxRecordLicenceSelectOption): boolean => option.value === currentValue)
    ) {
        return [...options]
    }

    const currentOption: Definable<CxRecordLicenceSelectOption> = buildCxrLicenceOptions(getLabel).find(
        (option: CxRecordLicenceSelectOption): boolean => option.value === currentValue
    )

    return currentOption ? [...options, currentOption] : [...options]
}

/**
 * Whether a loaded backend CXR subscription should be preserved rather than auto-normalised. *
 * Used for edit flows where the existing backend user has not yet been changed locally. Once either the
 * user type or the CXR field has been edited, restricted-user normalisation may apply again. *
 * @param loadedValue - Existing backend CXR subscription value remembered for the edited user.
 * @param currentValue - Current form value for the CXR subscription field.
 * @param isTypeTouched - Whether the user type field has been edited locally.
 * @param isSubscriptionTouched - Whether the CXR subscription field has been edited locally.
 * @returns True when the loaded backend value should remain visible and untouched.
 */
export function shouldPreserveLoadedCxrSubscription(
    loadedValue: Definable<CXRecordLicences>,
    currentValue: CXRecordLicences,
    isTypeTouched: boolean,
    isSubscriptionTouched: boolean
): boolean {
    return !!loadedValue && loadedValue === currentValue && !isTypeTouched && !isSubscriptionTouched
}

/**
 * Maps a disallowed CXR subscription to its replacement when the reporting level changes. *
 * When the current selection is {@link CXRecordLicences.CXR_MANAGER} and the reporting level does not
 * permit it ({@link isCxrManagerAllowedLevel} false — i.e. Historic, Real-Time or Agent), the replacement
 * is {@link CXRecordLicences.NONE}. All other values are unchanged. *
 * @param current - Current CXR subscription value.
 * @param reportingLevel - Current reporting level string from the form.
 * @returns Value to set on the form when the current tier is not allowed for the reporting level.
 */
export function cxrRestrictedReplacement(current: CXRecordLicences, reportingLevel: string): CXRecordLicences {
    if (current === CXRecordLicences.CXR_MANAGER && !isCxrManagerAllowedLevel(reportingLevel)) {
        return CXRecordLicences.NONE
    }

    return current
}
